import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Users } from "lucide-react";
import { travelersApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import LoadingState from "@/components/shared/LoadingState";
import ErrorState from "@/components/shared/ErrorState";
import { cn } from "@/lib/utils";

interface TravelerSummary {
  id: string;
  display_name: string;
  age?: number | null;
  relationship?: string | null;
  warmth?: number | null;
  is_self?: boolean;
}

function WarmthGauge({ value }: { value: number }) {
  const pct = Math.min(100, Math.max(0, value));
  return (
    <div className="space-y-1 mt-3">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>How well we know you</span>
        <span>{Math.round(pct)}%</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function Initials({ name }: { name: string }) {
  const parts = name.trim().split(/\s+/);
  const init =
    parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();
  return (
    <div className="h-12 w-12 rounded-full bg-primary/10 text-accent-foreground flex items-center justify-center text-sm font-semibold shrink-0">
      {init}
    </div>
  );
}

function AddTravelerForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");

  const mutation = useMutation({
    mutationFn: (body: { display_name: string; relationship?: string }) =>
      travelersApi.create(body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["travelers"] });
      onClose();
      if (data?.traveler?.id) {
        navigate(`/travelers/${data.traveler.id}`);
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    mutation.mutate({
      display_name: trimmed,
      ...(relationship.trim() ? { relationship: relationship.trim() } : {}),
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-4 rounded-lg border border-border bg-card p-4 space-y-3"
    >
      <h3 className="text-sm font-semibold">Add a traveler</h3>
      <div className="space-y-2">
        <input
          type="text"
          placeholder="Name (e.g. Ella)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          autoFocus
          required
        />
        <input
          type="text"
          placeholder="Relationship (optional — e.g. daughter, wife)"
          value={relationship}
          onChange={(e) => setRelationship(e.target.value)}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={mutation.isPending || !name.trim()}>
          {mutation.isPending ? "Adding…" : "Add traveler"}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        {mutation.isError && (
          <p className="text-xs text-destructive ml-auto">Failed — try again.</p>
        )}
      </div>
    </form>
  );
}

export default function TravelersList() {
  const [showForm, setShowForm] = useState(false);
  const queryClient = useQueryClient();

  // Ensure the "You" self traveler exists for this owner, then refresh the list.
  useEffect(() => {
    travelersApi.me().then(() => {
      queryClient.invalidateQueries({ queryKey: ["travelers"] });
    }).catch(() => { /* best-effort — never break the list view */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["travelers"],
    queryFn: travelersApi.list,
  });

  if (isLoading) return <LoadingState message="Loading travelers…" />;
  if (error) return <ErrorState message="Failed to load travelers" onRetry={() => refetch()} />;

  const travelers: TravelerSummary[] = data?.travelers ?? [];

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <h1 className="text-2xl font-bold tracking-tight">Your travelers</h1>
        </div>
        <Button
          size="sm"
          onClick={() => setShowForm((v) => !v)}
          className={cn(showForm && "opacity-50")}
        >
          <UserPlus className="h-4 w-4 mr-2" aria-hidden="true" />
          Add traveler
        </Button>
      </div>

      {showForm && <AddTravelerForm onClose={() => setShowForm(false)} />}

      {/* Empty state */}
      {travelers.length === 0 && !showForm && (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 p-10 text-center space-y-2">
          <Users className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">
            No travelers yet — add the people you travel with.
          </p>
          <Button size="sm" variant="outline" onClick={() => setShowForm(true)}>
            <UserPlus className="h-4 w-4 mr-2" aria-hidden="true" />
            Add your first traveler
          </Button>
        </div>
      )}

      {/* Grid */}
      {travelers.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {travelers.map((t) => (
            <Link
              key={t.id}
              to={`/travelers/${t.id}`}
              className="group rounded-lg border border-border bg-card p-4 hover:border-primary/40 hover:bg-accent/30 transition-colors flex flex-col"
            >
              <div className="flex items-center gap-3">
                <Initials name={t.display_name} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate group-hover:text-primary transition-colors">
                      {t.display_name}
                    </p>
                    {t.is_self && (
                      <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-accent-foreground">
                        You
                      </span>
                    )}
                  </div>
                  {(t.relationship || t.age != null) && (
                    <p className="text-xs text-muted-foreground capitalize truncate">
                      {[t.relationship, t.age != null ? `age ${t.age}` : null]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  )}
                </div>
              </div>
              {t.warmth != null && <WarmthGauge value={t.warmth} />}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
